import { useCallback } from "react";

import {
	useGetGeoCountriesMutation,
	useGetIndustriesQuery,
	useLazyGetPositionsQuery,
} from "@/pages/Private/redux/companyEnrich/companyEnrich.api";
import { CompanyEnrichAutoCompleteItem } from "@/pages/Private/pages/CompanyEnrich/components/CompanyEnrichAutoComplete";
import { Industry } from "@/pages/Private/pages/CompanyEnrich/schema/companyEnrich";

type Fetcher = (query: string) => Promise<CompanyEnrichAutoCompleteItem[]>;

export interface TrackFilterLookups {
	fetchPositions: Fetcher;
	fetchCountries: Fetcher;
	/** Every country in one call — turns stored ISO codes back into names. */
	fetchAllCountries: () => Promise<CompanyEnrichAutoCompleteItem[]>;
	industries: Industry[];
}

/**
 * The lookups the Track step's filter pane needs. Deliberately not
 * `useCompanyEnrichLookups` — that hook also loads geo regions and three pages
 * of countries the pane never shows, costing the onboarding step requests it
 * does not use.
 */
export const useTrackFilterLookups = (): TrackFilterLookups => {
	const [triggerGetPositions] = useLazyGetPositionsQuery();
	const [getGeoCountries] = useGetGeoCountriesMutation();
	const { data: industries } = useGetIndustriesQuery();

	const fetchPositions = useCallback<Fetcher>(
		async (query) => {
			const result = await triggerGetPositions({ query }).unwrap();

			return result.map((item) => ({ id: item, title: item }));
		},
		[triggerGetPositions]
	);

	const fetchCountries = useCallback<Fetcher>(
		async (query) => {
			const result = await getGeoCountries({ query }).unwrap();

			return result.items.map((item) => ({ id: item.code, title: item.name }));
		},
		[getGeoCountries]
	);

	// An empty query makes the endpoint return the full country list rather than
	// a search hit, so one request covers every code a saved filter can hold.
	const fetchAllCountries = useCallback(async (): Promise<CompanyEnrichAutoCompleteItem[]> => {
		const result = await getGeoCountries({}).unwrap();

		return result.items.map((item) => ({ id: item.code, title: item.name }));
	}, [getGeoCountries]);

	return { fetchPositions, fetchCountries, fetchAllCountries, industries: industries ?? [] };
};
